import { resolveCypeCatalogueCode } from "./cypeL37CatalogueMap.ts";
import {
  coverageFromEconomicValues,
  moneyCents,
  PRICE_ORIGINS,
  resolvePriceHierarchy,
  type CoverageBucket,
  type L37PriceOffer,
  type PriceOrigin,
} from "./priceOrigin.ts";

export const PILOT_SOURCE_CODES = ["EHS010", "EHV010", "EHM010"] as const;
export type PilotSourceCode = (typeof PILOT_SOURCE_CODES)[number];

export type ReferencePriceLine = {
  code: string;
  label?: string;
  unit: string;
  quantity: number;
  unit_price: number;
  partial?: number;
};

export type ResolvedCompositionLine = {
  key: string;
  resourceCode: string | null;
  label: string;
  unit: string;
  quantity: number | null;
  unitPrice: number | null;
  partial: number | null;
  priceOrigin: PriceOrigin;
  sourceCode: string | null;
  sourceUnit: string | null;
  sourceUnitPrice: number | null;
  resourcePriceId: string | null;
  note: string;
  includedInDryPu: boolean;
  labor: boolean;
};

export type ExcludedCompositionLine = {
  key: string;
  label: string;
  unit: string;
  quantity: number | null;
  cypePartial: number | null;
  note: string;
};

export type PilotPriceResolution = {
  sourceCode: PilotSourceCode;
  lines: ResolvedCompositionLine[];
  excluded: ExcludedCompositionLine[];
  dryUnitPrice: number | null;
  coverage: CoverageBucket[];
  missingResourceCodes: string[];
  cypeOriginalUnitPrice: number | null;
};

const FORCED_MISSING = new Set(["SUB.ACO.CORTE_DOBRAGEM", "EQ.BET.GRUA"]);

const LABOR = [
  { key: "mo044", code: "mo044", resourceCode: "MO.COFRADOR", label: "Cofrador" },
  { key: "mo091", code: "mo091", resourceCode: "MO.AJ.COFRADOR", label: "Ajudante de cofrador" },
  { key: "mo043", code: "mo043", resourceCode: "MO.ARMADOR", label: "Armador" },
  { key: "mo090", code: "mo090", resourceCode: "MO.AJ.ARMADOR", label: "Ajudante de armador" },
  { key: "mo045", code: "mo045", resourceCode: "MO.BETONAGEM", label: "Betonagem" },
] as const;

for (const labor of LABOR) {
  const mapped = resolveCypeCatalogueCode(labor.code);
  if (mapped?.l37Code !== labor.resourceCode || mapped.verdict !== "REUSE") {
    throw new Error("O mapa Set 1B divergiu do piloto BA em " + labor.code);
  }
}

type ArticleMap = {
  workLabel: string;
  steelCode: string;
  steelFallback: "fb_no" | "fb_ok";
  spacerCode: string;
  formworkResource: string;
  formworkLabel: string;
};

const ARTICLES: Record<PilotSourceCode, ArticleMap> = {
  EHS010: {
    workLabel: "Pilar",
    steelCode: "mt07aco040b",
    steelFallback: "fb_no",
    spacerCode: "mt07sep010ac",
    formworkResource: "AUX.COFR.REC_METAL",
    formworkLabel: "Cofragem recuperável metálica",
  },
  EHV010: {
    workLabel: "Viga",
    steelCode: "mt07aco040b",
    steelFallback: "fb_no",
    spacerCode: "mt07aco020c",
    formworkResource: "AUX.COFR.REC_MADEIRA",
    formworkLabel: "Cofragem recuperável em madeira",
  },
  EHM010: {
    workLabel: "Muro",
    steelCode: "mt07aco040e",
    steelFallback: "fb_ok",
    spacerCode: "mt07aco020d",
    formworkResource: "AUX.COFR.REC_METAL",
    formworkLabel: "Cofragem recuperável metálica",
  },
};

export function isPilotSourceCode(code: string | null | undefined): code is PilotSourceCode {
  return PILOT_SOURCE_CODES.includes(code as PilotSourceCode);
}

function numberParam(parameters: Record<string, string>, key: string) {
  const raw = parameters[key];
  if (!raw?.trim()) return null;
  const value = Number(raw.replace(",", "."));
  return Number.isFinite(value) && value > 0 ? value : null;
}

export function pilotFormworkArea(sourceCode: PilotSourceCode, parameters: Record<string, string>) {
  if (parameters.formwork_system === "none") return 0;
  if (sourceCode === "EHS010") {
    const a = numberParam(parameters, "section_a");
    const b = numberParam(parameters, "section_b");
    return a && b ? (2 * (a + b)) / (a * b) : null;
  }
  if (sourceCode === "EHV010") {
    const width = numberParam(parameters, "width");
    const height = numberParam(parameters, "height");
    return width && height ? (width + 2 * height) / (width * height) : null;
  }
  const thickness = numberParam(parameters, "thickness");
  const faces = parameters.formwork_faces === "one" ? 1 : parameters.formwork_faces === "two" ? 2 : null;
  return thickness && faces ? faces / thickness : null;
}

function formworkResource(parameters: Record<string, string>, article: ArticleMap) {
  if (parameters.formwork_system === "recoverable_wood") {
    return { code: "AUX.COFR.REC_MADEIRA", label: "Cofragem recuperável em madeira" };
  }
  if (parameters.formwork_system === "lost") {
    return { code: "AUX.COFR.PERDIDA", label: "Cofragem perdida" };
  }
  if (parameters.formwork_system === "recoverable_metal" || !parameters.formwork_system) {
    return { code: article.formworkResource, label: article.formworkLabel };
  }
  return { code: article.formworkResource, label: article.formworkLabel };
}

function concreteResource(parameters: Record<string, string>) {
  const strength = parameters.concrete_strength || "C25/30";
  return "MAT.BET." + strength.replace("/", "_");
}

function findLine(lines: ReferencePriceLine[], code: string) {
  return lines.find((line) => line.code === code) ?? null;
}

function hourText(value: number | null) {
  if (value === null) return "—";
  return value.toLocaleString("pt-PT", { maximumFractionDigits: 3 });
}

function laborPartials(quantities: number[], unitPrice: number) {
  const rounded = quantities.map((quantity) => moneyCents(quantity * unitPrice));
  const target = moneyCents(quantities.reduce((sum, quantity) => sum + quantity, 0) * unitPrice);
  const drift = Math.round(target * 100) - rounded.reduce((sum, partial) => sum + Math.round(partial * 100), 0);
  if (rounded.length && drift !== 0) {
    const last = rounded.length - 1;
    rounded[last] = moneyCents(rounded[last] + drift / 100);
  }
  return rounded;
}

export function resolvePilotConcreteArticle(input: {
  sourceCode: string | null | undefined;
  referenceLines: ReferencePriceLine[];
  parameters: Record<string, string>;
  offers: L37PriceOffer[];
  quantityOverrides?: Record<string, number | null>;
  cypeOriginalUnitPrice?: number | null;
}): PilotPriceResolution | null {
  const sourceCode = input.sourceCode;
  if (!isPilotSourceCode(sourceCode)) return null;
  const article = ARTICLES[sourceCode];
  const offers = input.offers;
  const lines = input.referenceLines ?? [];
  const override = input.quantityOverrides ?? {};
  const quantityOf = (code: string) => {
    if (Object.hasOwn(override, code)) return override[code];
    const source = findLine(lines, code);
    return source && Number.isFinite(Number(source.quantity)) ? Number(source.quantity) : null;
  };
  const resolved: ResolvedCompositionLine[] = [];

  const concreteLine = findLine(lines, "mt10haf020bgngc");
  const concreteCode = concreteResource(input.parameters);
  const concreteFallbackAllowed = concreteCode === "MAT.BET.C25_30" && concreteLine !== null;
  const concreteQty = quantityOf("mt10haf020bgngc");
  resolved.push(pricedLine({
    key: "concrete",
    resourceCode: concreteCode,
    label: "Betão " + (input.parameters.concrete_strength || "C25/30"),
    unit: "m3",
    quantity: concreteQty,
    offers,
    articleSourceCode: sourceCode,
    fallback: concreteFallbackAllowed && concreteLine ? {
      code: concreteLine.code,
      unit: concreteLine.unit,
      unitPrice: Number(concreteLine.unit_price),
    } : null,
    allowFallback: concreteFallbackAllowed,
    note: concreteFallbackAllowed
      ? "Preferir preço L37. O preço CYPE da linha só entra como fallback e nunca como preço L37."
      : "A classe escolhida não é a da linha CYPE desta ficha. Sem preço L37 compatível, a linha fica sem preço.",
  }));

  const steelLine = findLine(lines, article.steelCode);
  const steelCode = input.parameters.steel_grade && input.parameters.steel_grade !== "other"
    ? "MAT.ACO." + input.parameters.steel_grade
    : "MAT.ACO.A400NR";
  const steelFallbackAllowed = article.steelFallback === "fb_ok" && steelCode === "MAT.ACO.A400NR" && steelLine !== null;
  resolved.push(pricedLine({
    key: "steel",
    resourceCode: steelCode,
    label: "Aço " + (input.parameters.steel_grade || "A400NR"),
    unit: "kg",
    quantity: quantityOf(article.steelCode),
    offers,
    articleSourceCode: sourceCode,
    fallback: steelFallbackAllowed && steelLine ? {
      code: steelLine.code,
      unit: steelLine.unit,
      unitPrice: Number(steelLine.unit_price),
    } : null,
    allowFallback: steelFallbackAllowed,
    note: article.steelFallback === "fb_no"
      ? "O PU CYPE de fábrica inclui elaboração. Não é fallback do material e não gera corte/dobragem."
      : "Varão em obra. O preço L37 prevalece; o PU CYPE só entra como fallback compatível.",
  }));

  const steelQty = quantityOf(article.steelCode);
  resolved.push({
    key: "sub",
    resourceCode: "SUB.ACO.CORTE_DOBRAGEM",
    label: "Corte e dobragem de aço",
    unit: "kg",
    quantity: sourceCode === "EHM010" ? null : steelQty,
    unitPrice: null,
    partial: null,
    priceOrigin: "missing",
    sourceCode: null,
    sourceUnit: null,
    sourceUnitPrice: null,
    resourcePriceId: null,
    note: sourceCode === "EHM010"
      ? "Não aplicável ao varão em obra. Sem linha CYPE e sem preço."
      : "Sem reference_line. O diferencial face ao varão não é um preço.",
    includedInDryPu: false,
    labor: false,
  });

  const wire = findLine(lines, "mt08var050");
  resolved.push(pricedLine({
    key: "wire",
    resourceCode: "MAT.ACO.ARAME",
    label: "Arame de atar",
    unit: "kg",
    quantity: quantityOf("mt08var050"),
    offers,
    articleSourceCode: sourceCode,
    fallback: wire ? { code: wire.code, unit: wire.unit, unitPrice: Number(wire.unit_price) } : null,
    allowFallback: wire !== null,
    note: "Fallback CYPE só quando não há preço L37. A origem mantém o código, a unidade e o preço da ficha.",
  }));

  const spacer = findLine(lines, article.spacerCode);
  resolved.push(pricedLine({
    key: "spacer",
    resourceCode: "MAT.ACO.SEPARADOR",
    label: "Separadores",
    unit: "un",
    quantity: quantityOf(article.spacerCode),
    offers,
    articleSourceCode: sourceCode,
    fallback: spacer ? { code: spacer.code, unit: spacer.unit, unitPrice: Number(spacer.unit_price) } : null,
    allowFallback: spacer !== null,
    note: "Fallback CYPE da linha de separadores desta ficha. Não é preço L37.",
  }));

  const formwork = formworkResource(input.parameters, article);
  const formworkQty = pilotFormworkArea(sourceCode, input.parameters);
  if (formworkQty !== 0) {
    resolved.push(pricedLine({
      key: "formwork",
      resourceCode: formwork.code,
      label: formwork.label,
      unit: "m2",
      quantity: formworkQty,
      offers,
      articleSourceCode: sourceCode,
      fallback: null,
      allowFallback: false,
      note: "Utilização por m² geométrico. Os PU CYPE de painéis, escoras e acessórios amortizados não são copiados.",
    }));
  }

  const helperQty = quantityOf("mo092");
  let helperFused = false;
  const laborLines = LABOR.map((labor) => {
    const source = findLine(lines, labor.code);
    const ownQty = quantityOf(labor.code);
    const fallback = source ? { code: source.code, unit: source.unit, unitPrice: Number(source.unit_price) } : null;
    const preview = resolvePriceHierarchy({
      resourceCode: labor.resourceCode,
      unit: "h",
      offers,
      articleSourceCode: sourceCode,
      fallback,
      allowFallback: source !== null,
    });
    const fuseHelper = labor.resourceCode === "MO.BETONAGEM"
      && (preview.priceOrigin === "l37_validated" || preview.priceOrigin === "l37_provisional")
      && helperQty !== null
      && helperQty > 0;
    if (fuseHelper) helperFused = true;
    const quantity = fuseHelper ? (ownQty ?? 0) + helperQty : ownQty;
    return pricedLine({
      key: labor.key,
      resourceCode: labor.resourceCode,
      label: labor.label,
      unit: "h",
      quantity,
      offers,
      articleSourceCode: sourceCode,
      fallback,
      allowFallback: source !== null,
      note: fuseHelper
        ? "Fusão em MO.BETONAGEM: mo045 " + hourText(ownQty) + " h + mo092 " + hourText(helperQty)
          + " h. Preço L37 único. O PU horário CYPE de mo092 não é copiado."
        : "Horas da ficha. O preço L37 prevalece sobre o preço horário CYPE.",
      labor: true,
    });
  });
  if (!helperFused && helperQty !== null && helperQty > 0) {
    laborLines.push({
      key: "mo092",
      resourceCode: null,
      label: "Betonagem — ajudante (mo092)",
      unit: "h",
      quantity: helperQty,
      unitPrice: null,
      partial: null,
      priceOrigin: "missing",
      sourceCode: null,
      sourceUnit: null,
      sourceUnitPrice: null,
      resourcePriceId: null,
      note: "mo092 não tem recurso L37. Sem preço L37 em MO.BETONAGEM, as horas não são fundidas e o PU CYPE não é copiado.",
      includedInDryPu: false,
      labor: false,
    });
  }
  const laborByPrice = new Map<string, number[]>();
  laborLines.forEach((line, index) => {
    if (line.priceOrigin === "missing" || line.unitPrice === null || line.quantity === null) return;
    const groupKey = line.priceOrigin + "|" + line.unitPrice;
    const indexes = laborByPrice.get(groupKey) ?? [];
    indexes.push(index);
    laborByPrice.set(groupKey, indexes);
  });
  for (const indexes of laborByPrice.values()) {
    const sample = laborLines[indexes[0]];
    const partials = laborPartials(indexes.map((index) => laborLines[index].quantity ?? 0), sample.unitPrice ?? 0);
    indexes.forEach((index, offset) => {
      laborLines[index] = { ...laborLines[index], partial: partials[offset] };
    });
  }
  resolved.push(...laborLines);

  const placement = input.parameters.placement_method || "crane";
  if (placement === "crane") {
    resolved.push({
      key: "crane",
      resourceCode: "EQ.BET.GRUA",
      label: "Grua / balde",
      unit: "h",
      quantity: null,
      unitPrice: null,
      partial: null,
      priceOrigin: "missing",
      sourceCode: null,
      sourceUnit: null,
      sourceUnitPrice: null,
      resourcePriceId: null,
      note: "Betonagem com grua sem linha CYPE nesta ficha. Sem preço no piloto.",
      includedInDryPu: false,
      labor: false,
    });
  } else if (placement === "pump") {
    resolved.push(pricedLine({
      key: "pump",
      resourceCode: "EQ.BET.BOMBA",
      label: "Bomba de betão",
      unit: "h",
      quantity: null,
      offers,
      articleSourceCode: sourceCode,
      fallback: null,
      allowFallback: false,
      note: "Sem linha CYPE de bomba nesta ficha. Sem quantidade, o recurso não entra no PU.",
    }));
  }

  const excluded: ExcludedCompositionLine[] = lines
    .filter((line) => line.unit === "%" || line.code === "complement" || line.code === "auxiliary" || line.code === "indirect")
    .map((line) => ({
      key: line.code || "complement",
      label: line.label || "Complemento percentual",
      unit: "%",
      quantity: Number.isFinite(Number(line.quantity)) ? Number(line.quantity) : null,
      cypePartial: Number.isFinite(Number(line.partial)) ? Number(line.partial) : null,
      note: "Complemento percentual CYPE. Fica fora do PU seco L37 enquanto não existir regra L37.",
    }));

  const economic: Record<PriceOrigin, number> = {
    l37_validated: 0,
    l37_provisional: 0,
    cype_fallback: 0,
    missing: 0,
  };
  let includedCount = 0;
  for (const line of resolved) {
    if (!line.includedInDryPu || line.partial === null || line.priceOrigin === "missing") continue;
    economic[line.priceOrigin] = moneyCents(economic[line.priceOrigin] + line.partial);
    includedCount += 1;
  }
  const dryUnitPrice = includedCount > 0
    ? moneyCents(PRICE_ORIGINS.filter((origin) => origin !== "missing").reduce((sum, origin) => sum + economic[origin], 0))
    : null;
  return {
    sourceCode,
    lines: resolved,
    excluded,
    dryUnitPrice,
    coverage: coverageFromEconomicValues(economic, dryUnitPrice),
    missingResourceCodes: resolved.filter((line) => line.priceOrigin === "missing" && line.resourceCode).map((line) => line.resourceCode as string),
    cypeOriginalUnitPrice: input.cypeOriginalUnitPrice ?? null,
  };
}

function pricedLine(input: {
  key: string;
  resourceCode: string;
  label: string;
  unit: string;
  quantity: number | null;
  offers: L37PriceOffer[];
  articleSourceCode: PilotSourceCode;
  fallback: { code: string; unit: string; unitPrice: number } | null;
  allowFallback: boolean;
  note: string;
  labor?: boolean;
}): ResolvedCompositionLine {
  const forced = FORCED_MISSING.has(input.resourceCode);
  const price = resolvePriceHierarchy({
    resourceCode: input.resourceCode,
    unit: input.unit,
    offers: forced ? [] : input.offers,
    articleSourceCode: input.articleSourceCode,
    fallback: forced ? null : input.fallback,
    allowFallback: forced ? false : input.allowFallback,
    forceMissing: forced,
  });
  const quantity = input.quantity !== null && Number.isFinite(input.quantity) && input.quantity >= 0
    ? input.quantity
    : null;
  const included = price.priceOrigin !== "missing" && price.unitPrice !== null && quantity !== null && quantity > 0;
  const partial = included ? moneyCents(quantity * price.unitPrice!) : null;
  return {
    key: input.key,
    resourceCode: input.resourceCode,
    label: input.label,
    unit: input.unit,
    quantity,
    unitPrice: price.unitPrice,
    partial: input.labor ? partial : partial,
    priceOrigin: price.priceOrigin,
    sourceCode: price.sourceCode,
    sourceUnit: price.sourceUnit,
    sourceUnitPrice: price.sourceUnitPrice,
    resourcePriceId: price.resourcePriceId,
    note: input.note,
    includedInDryPu: included,
    labor: input.labor === true,
  };
}

export function priceResolutionSnapshot(resolution: PilotPriceResolution) {
  return {
    source_code: resolution.sourceCode,
    dry_unit_price: resolution.dryUnitPrice,
    cype_original_unit_price: resolution.cypeOriginalUnitPrice,
    missing_resource_codes: resolution.missingResourceCodes,
    coverage: resolution.coverage.map((bucket) => ({
      origin: bucket.origin,
      label: bucket.label,
      economic_value: bucket.economicValue,
      share_pct: bucket.sharePct,
    })),
    excluded: resolution.excluded,
    lines: resolution.lines.map((line) => ({
      key: line.key,
      resourceCode: line.resourceCode,
      label: line.label,
      unit: line.unit,
      quantity: line.quantity,
      unitPrice: line.unitPrice,
      partial: line.partial,
      priceOrigin: line.priceOrigin,
      sourceCode: line.sourceCode,
      sourceUnit: line.sourceUnit,
      sourceUnitPrice: line.sourceUnitPrice,
      resourcePriceId: line.priceOrigin === "cype_fallback" ? null : line.resourcePriceId,
      note: line.note,
      includedInDryPu: line.includedInDryPu,
    })),
  };
}
